export const ssr = false;

import type { LayoutLoadEvent } from './$types';
import { error, redirect } from '@sveltejs/kit';
import { FetchClientError } from '@foundatiofx/fetchclient';
import { ClientSettingsApi } from '#api/clientSettingsApi.ts';
import { UtilitiesApi } from '#api/utility.ts';
import { version, environment, isProd, hubSpotConfig } from '#lib/stores/stores.ts';
import { startDatadogRum } from '#lib/services/datadogRum.ts';

const legacyRedirects: Record<string, string> = {
    '/account/logon': '/login'
};

// Build metadata only changes on deploy, so one fetch per tab is enough — this load reruns on
// every refreshAll(). The await stays: initTimeTravel samples `isProd` once at startup.
let versionLoad: Promise<void> | undefined;

function loadVersion() {
    versionLoad ??= applyVersion().catch(() => {
        versionLoad = undefined;
    });

    return versionLoad;
}

async function applyVersion() {
    const result = await new UtilitiesApi().getVersion();

    version.set(result.data?.version ?? undefined);
    environment.set(result.data?.environment ?? undefined);
    hubSpotConfig.set(result.data?.hubSpot);
    void startDatadogRum(result.data?.datadogRum, result.data?.version);

    const env = result.data?.environment?.toLowerCase() ?? '';
    isProd.set(!env || (env !== 'staging' && env !== 'development'));

    // A failed fetch leaves isProd at its `true` default, which hides non-prod tooling — don't pin
    // that for the rest of the tab's life.
    if (!result.ok) versionLoad = undefined;
}

export const load = async (_e: LayoutLoadEvent) => {
    const mapped = legacyRedirects[_e.url.pathname.toLowerCase()];
    if (mapped) throw redirect(301, mapped + _e.url.search);

    await loadVersion();

    const settingsApi = new ClientSettingsApi();
    const { ok, data: clientSettings } = await settingsApi.getSettings().catch((e: unknown) => {
        if (e instanceof FetchClientError && e.status === 429)
            error(429, 'Too many requests from your network. Wait a minute, then reload the page.');
        throw e;
    });

    if (!ok || !clientSettings) throw new Error('Cannot determine client.');

    return { clientSettings };
};
